import { WebData, webDataNotAsked, unwrap, map } from "@lvt/web-data";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DateTime } from "luxon";

import { hourInSeconds } from "@/utilities/time";
import * as Types from "@/utilities/types/archive";


export interface archivesState {
    camerasOrCabinetsMeta: WebData<Types.CameraOrCabinetMetadata[]>;
    canRequestVideo: boolean;

    dateTimeHeaderHeight: number;
    //functional state
    gridAndAccordionWidth: number;
    images: Types.BucketImages;
    liveUnitId: null | number;
    // seconds
    requestDuration: number;
    // duration default value in seconds
    requestStartTime: string;
    // timestamp
    timeSpan: number;
    timelineStart: string;
    timelineWidth: number;
    //meta
    unitMeta: WebData<Types.ArchiveImagesNavMeta>;
    // timestamp
    videoSelectionMode: boolean;
}

const initialState: archivesState = {
    camerasOrCabinetsMeta: webDataNotAsked(),
    dateTimeHeaderHeight: 0,
    gridAndAccordionWidth: 0,
    images: {},
    canRequestVideo: true,
    liveUnitId: null,

    requestDuration: 4 * 60,

    //4 min in seconds
    requestStartTime: DateTime.now().plus({ minutes: 30 }).toISO(),

    timeSpan: 3600,
    unitMeta: webDataNotAsked(),
    timelineStart: DateTime.now().minus({ hours: 1 }).toISO(),
    timelineWidth: 0,
    videoSelectionMode: false,
};

const videoRequestMinimumValue = 60; //In Seconds
const videoRequestMaximumValue = 1800; //In Seconds

const archivesSlice = createSlice({
    name: "archives",
    initialState,
    reducers: {
        setCamerasOrCabinetsMeta(state, action: PayloadAction<WebData<Types.CameraOrCabinetMetadata[]>>) {
            state.camerasOrCabinetsMeta = action.payload;
        },
        setDateTimeHeaderHeight(state, action: PayloadAction<number>) {
            state.dateTimeHeaderHeight = action.payload;
        },
        setBucketImage(
            state,
            action: PayloadAction<{ presetId: number; startTime: number; images: WebData<Types.ArchiveImage[]> }>,
        ) {
            const bucket = action.payload.startTime;

            if (bucket % hourInSeconds === 0) {
                const presetId = action.payload.presetId;
                const webDataImages = action.payload.images;

                if (!state.images[bucket]) {
                    state.images[bucket] = {};
                }

                state.images[bucket][presetId] = webDataImages;
            } else {
                console.error("AHHHHHHH", action);
                //TODO: yell?
            }
        },

        // Used to set multiple presets in a bucket all at once, etc
        setBucketImages(state, action: PayloadAction<{ presetsToImages: Types.PresetsToImages; startTime: number }>) {
            const bucket = action.payload.startTime;

            if (bucket % hourInSeconds === 0) {
                Object.entries(action.payload.presetsToImages).map(([presetIdStr, webDataImages]) => {
                    const base = 10;
                    const presetId = parseInt(presetIdStr, base);

                    if (!state.images[bucket]) {
                        state.images[bucket] = {};
                    }

                    state.images[bucket][presetId] = webDataImages;
                });
            } else {
                console.error("AHHHHHHH", action);
                //TODO: yell?
            }
        },

        setGridAndAccordionWidth(state, action: PayloadAction<number>) {
            state.gridAndAccordionWidth = action.payload;
        },

        setCanRequestVideo(state, action: PayloadAction<boolean>) {
            state.canRequestVideo = action.payload;
        },

        setLiveUnitId(state, action: PayloadAction<number | null>) {
            state.liveUnitId = action.payload;
        },

        setRequestDuration(state, action: PayloadAction<number>) {
            if (action.payload <= videoRequestMinimumValue) {
                state.requestDuration = videoRequestMinimumValue;
            } else if (action.payload >= videoRequestMaximumValue) {
                state.requestDuration = videoRequestMaximumValue;
            } else {
                const requestDateTimePlusDuration = DateTime.fromISO(state.requestStartTime).plus({
                    seconds: action.payload,
                });
                const timelineStartPlusHour = DateTime.fromISO(state.timelineStart).plus({ hours: 1 });

                if (requestDateTimePlusDuration < timelineStartPlusHour) {
                    state.requestDuration = action.payload;
                }
            }
        },

        setRequestStartTime(state, action: PayloadAction<string>) {
            const payloadDateTime = DateTime.fromISO(action.payload);
            const defaultStart = DateTime.now().minus({ days: 90 }).toISO(); // the default video recording start

            const recordingStartIso = unwrap(map(state.unitMeta, (unitMeta) => unitMeta.recordingStart));
            const recordingStart = DateTime.fromISO(recordingStartIso ? recordingStartIso : defaultStart).toSeconds();

            if (payloadDateTime.isValid) {
                if (
                    payloadDateTime.toSeconds() + state.requestDuration <= DateTime.now().toSeconds() &&
                    payloadDateTime.toSeconds() >= recordingStart
                )
                    state.requestStartTime = payloadDateTime.toISO();
            } else {
                state.requestStartTime = action.payload;
            }
        },

        setTimeSpan(state, action: PayloadAction<number>) {
            state.timeSpan = action.payload;
        },

        setTimelineStart(state, action: PayloadAction<string>) {
            const payloadDateTime = DateTime.fromISO(action.payload); //+-30 minutes
            const recordingStartIso = unwrap(map(state.unitMeta, (unitMeta) => unitMeta.recordingStart));

            if (state.videoSelectionMode && recordingStartIso) {
                const diff = DateTime.fromISO(state.requestStartTime).diff(DateTime.fromISO(state.timelineStart));
                const newRequestStart = DateTime.min(
                    //min of the below min comparison and max end time for video request factoring the duration of the request
                    DateTime.min(
                        // min of below and the max timelineStart possible
                        DateTime.max(DateTime.fromISO(recordingStartIso), payloadDateTime), //maximum of recordingStart and datetime we want to set(results in it being capped in the past)
                        DateTime.now().minus({ hours: 1 }),
                    ).plus(diff), //converts the timelinestart concept to requestStartTime concept
                    DateTime.now().minus(state.requestDuration),
                );

                state.requestStartTime = newRequestStart.toISO();
            }

            if (payloadDateTime.isValid) {
                //Taking the min here because payloaddatetime could be a future value so we'd want to cap the timeline start to now - 1hr
                let newDateTime = DateTime.min(payloadDateTime, DateTime.now().minus({ hours: 1 }));

                if (state.videoSelectionMode && recordingStartIso) {
                    newDateTime = DateTime.max(DateTime.fromISO(recordingStartIso), newDateTime);
                }

                state.timelineStart = newDateTime.toISO();
            } else {
                state.timelineStart = action.payload;
            }
        },

        setTimelineWidth(state, action: PayloadAction<number>) {
            state.timelineWidth = action.payload;
        },

        setUnitMeta(state, action: PayloadAction<WebData<Types.ArchiveImagesNavMeta>>) {
            state.unitMeta = action.payload;
        },
        setVideoSelectionMode(state, action: PayloadAction<boolean>) {
            state.videoSelectionMode = action.payload;
        },
    },
});

export const {
    setCamerasOrCabinetsMeta,
    setLiveUnitId,
    setGridAndAccordionWidth,
    setDateTimeHeaderHeight,
    setRequestDuration,
    setRequestStartTime,
    setTimelineStart,
    setTimelineWidth,
    setTimeSpan,
    setUnitMeta,
    setVideoSelectionMode,
    setBucketImage,
    setBucketImages,
    setCanRequestVideo,
} = archivesSlice.actions;
export const archivesReducer = archivesSlice.reducer;
